import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplet, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import GallerySection from "../components/GallerySection";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Common";
import { IMAGES, OUD_OIL_GRADES, OUD_OIL_GALLERY, waLink } from "../mock/mock";

const oilJsonLd = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Pure Oud Oil (Agarwood Oil)",
  image: IMAGES.attarBottlesGold,
  description: "Precious pure oud oil distilled from the heartwood of the Aquilaria tree, available in Grade A, Grade B and Boya. Distilled across 27 in-house units in North East India.",
  brand: { "@type": "Brand", name: "Meilleur Oudh & Aromas" },
  category: "Agarwood / Oud Oil",
  offers: {
    "@type": "AggregateOffer",
    availability: "https://schema.org/InStock",
    priceCurrency: "USD",
    seller: { "@type": "Organization", name: "Meilleur Oudh & Aromas" },
  },
};

const OudOil = () => {
  return (
    <div className="bg-noir">
      <Seo
        title="Oud Oil — Pure Agarwood Oil, Grade A, B & Boya | Meilleur"
        description="Pure oud oil (agarwood oil) distilled from Aquilaria heartwood, in Grade A, Grade B and Boya. A coveted ingredient in premium perfumery. Worldwide trade & export."
        path="/oud-oil"
        keywords="oud oil, agarwood oil, pure oud oil, buy oud oil, Grade A oud oil, Boya, oud oil supplier, dehnal oud, oud oil exporter"
        jsonLd={oilJsonLd}
      />
      <PageHero eyebrow="Meilleur Oudh & Aromas" title="Oud" accent="Oil" image={IMAGES.attarBottlesGold} crumbs={["Collections", "Oud Oil"]} />

      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <div className="img-zoom relative">
                <img src={IMAGES.perfumeArabic} alt="Oud oil bottle" className="w-full h-[560px] object-cover" />
                <div className="absolute inset-0 border border-[var(--line)]" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal><SectionHeading eyebrow="Distilled Purity" title="A symbol of opulence" accent="& refinement" /></Reveal>
            <Reveal delay={120}>
              <div className="mt-8 space-y-6 text-[var(--ivory-muted)] text-lg leading-relaxed font-body font-light">
                <p>
                  A precious and highly sought-after essential oil extracted from the heartwood of the Aquilaria tree.
                  Renowned for its deep, rich and complex aroma, oud oil is used in perfumery, aromatherapy and religious
                  ceremonies.
                </p>
                <p>
                  Distilled across our 27 in-house units at a capacity of one litre per unit each month, it is a coveted
                  ingredient in the world's premium fragrances.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Grades */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="Three Distillations" title="Grades of" accent="oud oil" align="center" /></Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-7">
            {OUD_OIL_GRADES.map((g, i) => (
              <Reveal key={g.name} delay={i * 120}>
                <div className="lux-card p-10 h-full text-center group">
                  <div className="w-16 h-16 mx-auto rounded-full border border-[var(--line)] flex items-center justify-center mb-7 group-hover:border-[var(--gold)] transition-colors">
                    <Droplet size={24} className="text-[var(--gold)]" />
                  </div>
                  <h3 className="font-serif-display text-3xl mb-4 gold-text">{g.name}</h3>
                  <div className="hairline opacity-40 w-16 mx-auto mb-5" />
                  <p className="text-sm text-[var(--ivory-muted)] font-body leading-relaxed">{g.profile}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="Tree to Trade" title="Our" accent="process" /></Reveal>
          <div className="mt-16 grid md:grid-cols-4 gap-8">
            {[
              { s: "01", t: "Cultivate", d: "Aquilaria grown across 103 acres in Assam, Tripura & Nagaland." },
              { s: "02", t: "Inoculate", d: "Resin formation triggered — the driver of oud value." },
              { s: "03", t: "Distil", d: "Heartwood distilled across 27 in-house units." },
              { s: "04", t: "Grade & Ship", d: "Sorted into A, B & Boya, exported worldwide." },
            ].map((p, i) => (
              <Reveal key={p.s} delay={i * 100}>
                <div className="border-t border-[var(--gold)] pt-6">
                  <div className="font-serif-display text-5xl gold-text opacity-30 mb-4">{p.s}</div>
                  <h3 className="font-serif-display text-2xl mb-2">{p.t}</h3>
                  <p className="text-sm text-[var(--ivory-muted)] font-body leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <GallerySection
        eyebrow="The Gallery"
        title="Inspect the"
        accent="oil"
        images={OUD_OIL_GALLERY}
        label="Oud Oil"
      />

      <section className="pb-32">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="border border-[var(--line)] p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="font-serif-display text-3xl md:text-4xl">Source <span className="gold-text italic">pure oud oil</span></h2>
                <p className="text-[var(--ivory-muted)] mt-3 font-body font-light">Grade A · Grade B · Boya — available for trade partners.</p>
              </div>
              <div className="flex flex-wrap gap-4 shrink-0">
                <a href={waLink("Oud Oil")} target="_blank" rel="noopener noreferrer" className="btn-gold">
                  <MessageCircle size={15} /> WhatsApp Enquiry
                </a>
                <Link to="/contact" className="btn-ghost">Request a Quote <ArrowRight size={15} /></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default OudOil;
